import React, { useCallback } from 'react';
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import { MultiSelectTopicCell } from '../MultiSelectTopicCell';
import { StyledBadge } from '../StyledBadge';
import { CreatorInfo } from '../CreatorInfo';
import { ExternalLink, CalendarIcon } from "lucide-react";
import { KolDataWithDates } from '@/types/table';
import { EditableCell } from './EditableCell';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Option {
  value: string;
  label: string;
}

interface CellRenderersProps {
  updateRow: (id: string, field: string, value: any) => Promise<void>;
  handleToggleIntegration: (rowData: KolDataWithDates, checked: boolean) => void;
  handlePicChange: (row: KolDataWithDates, selectedPics: string[]) => void;
  getContentTypeOptionsForKol: (kolDbId: string | undefined) => string[];
  getKolDbInfo: (kolDbId: string | undefined) => any;
  getTypeVariant: (type: string) => "blue" | "red" | "purple" | "green" | "yellow";
  getAllCampaignsAndTopics: () => Array<{value: string; label: string; type: 'Campaign' | 'Topic'}>;
  picOptions: Option[];
  hasFullAccess: boolean;
  PLATFORM_OPTIONS: string[];
  POST_TYPE_OPTIONS: string[];
  TIER_OPTIONS: string[];
  LANGUAGE_OPTIONS: string[];
  CONTENT_TYPE_OPTIONS: string[];
  isEditable?: boolean; // Add this prop to control editability
}

export const useKolPostingLinksCellRenderers = ({
  updateRow,
  handleToggleIntegration,
  handlePicChange,
  getContentTypeOptionsForKol,
  getKolDbInfo,
  getTypeVariant,
  getAllCampaignsAndTopics,
  picOptions,
  hasFullAccess,
  PLATFORM_OPTIONS,
  POST_TYPE_OPTIONS,
  TIER_OPTIONS,
  LANGUAGE_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  isEditable = true // Default to editable
}: CellRenderersProps) => {

  // Editable text cell renderer with save/cancel buttons
  const renderEditableTextCell = useCallback((
    row: KolDataWithDates, 
    field: keyof KolDataWithDates,
    placeholder?: string
  ) => {
    const currentValue = (row[field] as string) || "";
    
    const handleSave = async (value: string | number) => {
      await updateRow(row.id, field, value);
    };

    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <EditableCell
          value={currentValue}
          onSave={handleSave}
          placeholder={placeholder || `Enter ${String(field)}`}
          inputType="text"
          className="w-full"
          disabled={!isEditable} // Use isEditable prop
          row={row}
          field={String(field)}
        />
      </div>
    );
  }, [updateRow, isEditable]);

  // Editable number cell renderer with save/cancel buttons
  const renderEditableNumberCell = useCallback((
    row: KolDataWithDates, 
    field: keyof KolDataWithDates,
    placeholder?: string
  ) => {
    const currentValue = (row[field] as number) || 0;
    
    const handleSave = async (value: string | number) => {
      await updateRow(row.id, field, value);
    };

    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <EditableCell
          value={currentValue}
          onSave={handleSave}
          placeholder={placeholder || "0"}
          inputType="number"
          className="w-full"
          disabled={!isEditable} // Use isEditable prop
          row={row}
          field={String(field)}
        />
      </div>
    );
  }, [updateRow, isEditable]);

  // Editable URL cell renderer with save/cancel buttons and external link
  const renderEditableUrlCell = useCallback((
    row: KolDataWithDates, 
    field: keyof KolDataWithDates,
    placeholder?: string
  ) => {
    const currentValue = (row[field] as string) || "";
    
    const handleSave = async (value: string | number) => {
      await updateRow(row.id, field, value);
    };

    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        {!currentValue ? (
          <EditableCell
            value={currentValue}
            onSave={handleSave}
            placeholder={placeholder || "Enter URL"}
            inputType="url"
            className="w-full"
            disabled={!isEditable} // Use isEditable prop
            row={row}
            field={String(field)}
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <EditableCell
                value={currentValue}
                onSave={handleSave}
                placeholder={placeholder || "Enter URL"}
                inputType="url"
                className="w-full"
                disabled={!isEditable} // Use isEditable prop
                row={row}
                field={String(field)}
              />
            </div>
            <a
              href={currentValue}
              target="_blank"
              rel="noopener noreferrer"
              className="dark:text-white light:text-blue-600 hover:underline flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>
    );
  }, [updateRow, isEditable]);

  // Select cell renderer (immediate save for dropdowns)
  const renderEditableCell = useCallback((
    row: KolDataWithDates, 
    field: keyof KolDataWithDates, 
    options: string[], 
    badgeVariant?: string,
    placeholder?: string
  ) => {
    const currentValue = row[field] as string;
    
    const handleChange = async (value: string) => {
      if (!isEditable) return; // Prevent changes if not editable
      console.log(`Cell renderer: Changing ${String(field)} to:`, value);
      await updateRow(row.id, field, value);
    };
    
    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <SearchableSelect
          options={options}
          value={currentValue}
          onChange={handleChange}
          placeholder={placeholder || `Select ${String(field)}`}
          badgeVariant={badgeVariant as any}
          className="w-full max-w-full"
          clearable={true}
          disabled={!isEditable} // Use isEditable prop
        />
      </div>
    );
  }, [updateRow, isEditable]);

  // Render text cell with truncation and tooltip
  const renderTextCell = useCallback((
    text: string | null | undefined,
    maxLength: number = 20
  ) => {
    if (!text) {
      return <span className="text-muted-foreground text-xs">-</span>;
    }
    
    const displayText = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    
    return (
      <span className="text-xs truncate block max-w-full cursor-default" title={text}>
        {displayText}
      </span>
    );
  }, []);

  // Editable date cell renderer with date picker
  const renderEditableDateCell = useCallback((
    row: KolDataWithDates,
    field: keyof KolDataWithDates
  ) => {
    const currentDate = row[field] as Date | null;
    
    const handleDateChange = async (date: Date | undefined) => {
      if (!isEditable) return; // Prevent changes if not editable
      await updateRow(row.id, field, date || null);
    };

    // If not editable, show read-only date display
    if (!isEditable) {
      return (
        <div className="w-full max-w-full overflow-hidden px-1 py-1">
          <div className="py-2 px-3 text-muted-foreground text-xs">
            {currentDate ? format(currentDate, "PPP") : "No date set"}
          </div>
        </div>
      );
    }

    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal h-8 text-xs",
                !currentDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-3 w-3" />
              {currentDate ? format(currentDate, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={currentDate || undefined}
              onSelect={handleDateChange}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }, [updateRow, isEditable]);

  // Multi-select Content ID cell renderer (simplified without cascading logic)
  const renderMultiSelectCampaignBuCell = useCallback((row: KolDataWithDates) => {
    return (
      <MultiSelectTopicCell
        row={row}
        getAllCampaignsAndTopics={getAllCampaignsAndTopics}
        updateRow={updateRow}
      />
    );
  }, [getAllCampaignsAndTopics, updateRow]);

  // Campaign BU cell renderer (keeping for backward compatibility)
  const renderCampaignBuCell = useCallback((row: KolDataWithDates) => {
    // Handle both array and string types for campaign_bu
    const currentValue = Array.isArray(row.campaign_bu) ? row.campaign_bu[0] || '' : row.campaign_bu || '';
    
    const handleChange = async (value: string) => {
      console.log(`Campaign BU: Direct change to:`, value);
      // For backward compatibility, store as array but accept single value
      await updateRow(row.id, 'campaign_bu', [value]);
    };
    
    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <SearchableSelect
          options={[]} // Empty options since we're not using cascading logic
          value={currentValue}
          onChange={handleChange}
          placeholder="Select content ID"
          badgeVariant="default"
          className="w-full max-w-full"
          clearable={true}
        />
      </div>
    );
  }, [updateRow]);

  // Business Unit cell renderer (simplified without cascading logic)
  const renderBusinessUnitCell = useCallback((row: KolDataWithDates) => {
    const currentValue = row.business_unit;
    
    const handleChange = async (value: string) => {
      console.log(`Business Unit: Direct change to:`, value);
      await updateRow(row.id, 'business_unit', value);
    };
    
    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <SearchableSelect
          options={[]} // Empty options since we're not using cascading logic
          value={currentValue}
          onChange={handleChange}
          placeholder="Select business unit"
          badgeVariant="primary"
          className="w-full max-w-full"
          clearable={true}
        />
      </div>
    );
  }, [updateRow]);

  // Content ID cell renderer with improved UI
  const renderContentIdCell = useCallback((row: KolDataWithDates) => {
    // Handle both array and string types for campaign_bu (Content ID)
    const currentValue = Array.isArray(row.campaign_bu) ? row.campaign_bu[0] || '' : row.campaign_bu || '';
    
    const handleChange = async (value: string) => {
      if (!isEditable) return; // Prevent changes if not editable
      console.log(`Content ID: Direct change to:`, value);
      // Store as array for backward compatibility
      await updateRow(row.id, 'campaign_bu', [value]);
    };
    
    return (
      <div className="w-full max-w-full overflow-hidden px-1 py-1">
        <SearchableSelect
          options={[]} // This will be populated by the column config
          value={currentValue}
          onChange={handleChange}
          placeholder="Select Content ID"
          badgeVariant="blue"
          className="w-full max-w-full"
          clearable={true}
          disabled={!isEditable} // Use isEditable prop
        />
      </div>
    );
  }, [updateRow, isEditable]);

  return {
    renderEditableTextCell,
    renderEditableNumberCell,
    renderEditableUrlCell,
    renderEditableCell,
    renderTextCell,
    renderEditableDateCell,
    renderMultiSelectCampaignBuCell,
    renderCampaignBuCell,
    renderBusinessUnitCell,
    renderContentIdCell
  };
};
